import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { formatDateTime } from '../../services/expenseVouchersApi';
import { AGEING_OPTIONS, formatMoney, reportsApi } from '../../services/expenseAccountsApi';
import { ReportsHeader, useReportsBase } from '../../components/expense/ReportShared';

// [key, label, report sub-path or null, color]
const STATUS_CARDS = [
  ['pendingAccounts', 'Pending Accounts', null, 'info.main'],
  ['pendingApproval', 'Pending Approval', null, 'info.main'],
  ['sentBack', 'Sent Back', null, 'warning.main'],
  ['approvedUnprocessed', 'Approved / Unprocessed', null, 'text.primary'],
  ['inProcess', 'In Process', 'payables', 'info.main'],
  ['partiallyPaid', 'Partially Paid', 'payables', 'warning.main'],
  ['onHold', 'On Hold', 'payables', 'secondary.main'],
  ['paid', 'Paid', 'payments', 'success.main'],
  ['cancelled', 'Cancelled', null, 'error.main'],
  ['unsettledAdvances', 'Advances Unsettled', 'advances', 'warning.main'],
  ['settlementPending', 'Settlement Pending', 'advances', 'info.main'],
];

const CountCard = ({ label, value, color, to }) => {
  const body = (
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value}</Typography>
    </CardContent>
  );
  return (
    <Card variant="outlined" sx={{ height: '100%' }} data-testid={`count-${label}`}>
      {to ? <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>{body}</CardActionArea> : body}
    </Card>
  );
};

const head = (cells) => (
  <TableHead>
    <TableRow sx={{ backgroundColor: 'primary.main' }}>
      {cells.map((c, i) => <TableCell key={c} align={i ? 'right' : 'left'} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>{c}</TableCell>)}
    </TableRow>
  </TableHead>
);

// Accounts Dashboard: counts from the real voucher statuses and money per currency (never a
// combined total across currencies). Computed on the server in a few aggregations.
const AccountsReportsDashboard = () => {
  const base = useReportsBase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await reportsApi.dashboard()).data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader
        title="Accounts Dashboard"
        subtitle={data ? `As of ${formatDateTime(data.generatedAt)}` : 'Expense processing at a glance.'}
        actions={<Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} sx={{ textTransform: 'none' }}>Refresh</Button>}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load the dashboard: {error.message}</Alert>}
      {loading && !data && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

      {data && (
        <>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {STATUS_CARDS.map(([key, label, sub, color]) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={key}>
                <CountCard label={label} value={data.counts[key]} color={color} to={sub ? `${base}/${sub}` : null} />
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Outstanding payables by currency</Typography>
          {data.outstandingPayables.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No outstanding payables.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 2 }}>
              <Table size="small" aria-label="Outstanding payables by currency">
                {head(['Currency', 'Vouchers', 'Net Payable', 'Paid', 'Balance Payable', ...AGEING_OPTIONS.map((o) => o.label)])}
                <TableBody>
                  {data.outstandingPayables.map((r) => (
                    <TableRow key={r.currency}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.currency}</TableCell>
                      <TableCell align="right">{r.vouchers}</TableCell>
                      <TableCell align="right">{formatMoney(r.netPayable, r.currency)}</TableCell>
                      <TableCell align="right">{formatMoney(r.paid, r.currency)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(r.balancePayable, r.currency)}</TableCell>
                      {AGEING_OPTIONS.map((o) => {
                        const b = r.ageing.find((x) => x.bucket === o.value);
                        return <TableCell key={o.value} align="right">{b && b.count ? `${formatMoney(b.balance, r.currency)} (${b.count})` : '-'}</TableCell>;
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Wallet positions by currency (actual)</Typography>
          {(data.walletPositions || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No employee wallets.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 2 }}>
              <Table size="small" aria-label="Wallet positions by currency">
                {head(['Currency', 'Recoverable from employees', 'Employees owing', 'Payable to employees', 'Employees owed'])}
                <TableBody>
                  {data.walletPositions.map((r) => (
                    <TableRow key={r.currency}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <RouterLink to={`${base}/wallets`} style={{ color: 'inherit' }}>{r.currency}</RouterLink>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(r.recoverable, r.currency)}</TableCell>
                      <TableCell align="right">{r.recoverableEmployees}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: r.payable ? 'error.main' : undefined }}>{formatMoney(r.payable, r.currency)}</TableCell>
                      <TableCell align="right">{r.payableEmployees}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Advances by currency</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Expected advance-vs-settlement differences (not cash). The actual money position is the wallet table above.
          </Typography>
          {data.advances.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No open advances.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table size="small" aria-label="Advances by currency">
                {head(['Currency', 'Unsettled', 'Settlement in progress', 'Settled', 'Credited to Wallet', 'Expected Recoverable', 'Expected Additional Payable'])}
                <TableBody>
                  {data.advances.map((r) => (
                    <TableRow key={r.currency}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.currency}</TableCell>
                      <TableCell align="right">{r.unsettled}</TableCell>
                      <TableCell align="right">{r.inSettlement}</TableCell>
                      <TableCell align="right">{r.settled}</TableCell>
                      <TableCell align="right">{formatMoney(r.credited, r.currency)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(r.expectedRecoverable, r.currency)}</TableCell>
                      <TableCell align="right">{formatMoney(r.expectedAdditionalPayable, r.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default AccountsReportsDashboard;
